import { ReactComponent as DataElementTopRightSquare } from "@/assets/svgs/arrow-top-right-on-square.svg";
import { ReactComponent as IconDelete } from "@/assets/svgs/delete.svg";
import { ReactComponent as IconDownloadAll } from "@/assets/svgs/download-all-folder.svg";
import { ReactComponent as DownloadWhite } from "@/assets/svgs/download-white.svg";
import { ReactComponent as IconClose } from '@/assets/svgs/icon-close.svg';
import { ReactComponent as IconTrashBin } from "@/assets/svgs/icon-trash-bin.svg";
import { ReactComponent as IconTick } from "@/assets/svgs/tick.svg";
import { ReactComponent as UploadIcon } from "@/assets/svgs/upload-icon.svg";
import Button from '@/components/Button';
import CardConfigure from '@/componentsDS/Modal/CardConfigure';
import Modal from '@/componentsDS/Modal/Modal';
import SplitFileType from "@/components/SplitFileType/index";
import Toast2 from '@/components/Toast2';
import fileExtensions from "@/constants/file-extensions/index";
import apiDownload from "@/services/api/download.js";
import rem from '@/utils/rem';
import classname from "classnames";
import { produce } from "immer";
import { uniqueId } from 'lodash';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useState } from "react";
import ReactDOM from 'react-dom';
import { useDropzone } from 'react-dropzone';

const Checkbox = ({ ['data-testid']: dataTestId, selected = false, handleUpdateItemSelect = null, disabled = false, indeterminate = false }) => {
	const checkBoxClass = classname({
		"attachment-card__checkbox": true,
		"attachment-card__checkbox--active": selected && !disabled,
		"attachment-card__checkbox--disabled": disabled,
		"attachment-card__checkbox--indeterminate": indeterminate,
	});

	const handleOnClick = (e) => {
		e.stopPropagation();
		if (disabled) return;
		if (handleUpdateItemSelect) handleUpdateItemSelect(!selected);
	};

	return (
		<div
			data-testid={dataTestId}
			className={checkBoxClass}
			onClick={handleOnClick}
			onKeyPress={handleOnClick}
			tabIndex={0}
		>
			{selected && !disabled ? <IconTick /> : null}
		</div>
	);
};

Checkbox.propTypes = {
	'data-testid': PropTypes.string,
	selected: PropTypes.bool,
	handleUpdateItemSelect: PropTypes.func,
	disabled: PropTypes.bool,
	indeterminate: PropTypes.bool
};

const AttachmentCard = ({
	attachmentList,
	setAttachmentList,
	setUploadAttachment = null,
	setDeleteAttachment = null,
	stateType = "view",
	makerChecker = false,
	dataElement = "attachment",
	attachmentExtensions = null,
	previousAttachmentList,
}) => {

	const [loading, setLoading] = useState(false)
	const [isChecked, setIsChecked] = useState(false)

	/*=================================================
		Error Modal
	/================================================*/
	const [error, setError] = useState("")

	function handleError() {
		setError("")
	}

	const onDropAttachment = useCallback(acceptedFiles => {
		const newFiles = acceptedFiles.map(file => Object.assign(file, {
			preview: URL.createObjectURL(file)
		}))

		if (newFiles.length === 0) {
			setError(attachmentExtensions ? attachmentExtensions?.errorMessage : fileExtensions.errorMessage)
			return
		}

		setAttachmentList(oldFiles => {
			if (oldFiles?.some((attachmentFile) => (attachmentFile.filename === newFiles[0].name || attachmentFile.name === newFiles[0].name))) {
				setError(`You have uploaded the same file.`)
				return oldFiles
			}

			const checkedOldFiles = oldFiles.filter(oldFile => !newFiles.find(newFile => newFile.name === oldFile.name))
			const sumAllFiles = checkedOldFiles.reduce((prev, next) => { return prev + next.size }, 0);
			if ((sumAllFiles + newFiles[0].size) > 250000000) {
				setError("File uploaded size is more than 250MB")
				return checkedOldFiles
			}
			const allFiles = checkedOldFiles.concat(newFiles)
			return allFiles
		})
	}, [])

	const { getRootProps: getRootPropsAttachment, getInputProps: getInputPropsAttachment } = useDropzone({ onDrop: onDropAttachment, multiple: false, accept: (attachmentExtensions ? attachmentExtensions?.type : fileExtensions.type) })

	function renderUploadAttachment() {
		return (
			<div className="attachment-card__btn-upload-attachment" data-testid="upload-attachment" {...getRootPropsAttachment()}>
				<input
					{...getInputPropsAttachment()}
					data-testid="attachment-dropzone"
				/>{/* */}
				<div className="attachment-card__btn-icon DS__icon--theme"><UploadIcon /></div>
				<div className="attachment-card__btn-text">Upload</div>
			</div>
		);
	}

	/*=================================================
		Checkbox Function
	/================================================*/

	useEffect(() => {
		const selectedAttachment = attachmentList?.length && attachmentList?.some((item) => item?.$selected === true)
		setIsChecked(selectedAttachment)
	}, [attachmentList])

	const isAllChecked = (itemData) => {
		return itemData?.length && itemData?.every((item) => item?.$selected === true || item?.$disabled === true) && itemData?.some((item) => item?.$selected === true);
	};

	const handleSingleAttachmentSelect = (item) => {
		if (item?.$disabled) return;

		const index = attachmentList?.findIndex(
			(attachment) => attachment && attachment.id === item?.id
		);

		const updatedItems = produce(attachmentList, (draft) => {
			draft[index].$selected = !draft[index].$selected;
		});

		setAttachmentList(updatedItems);
	};

	const handleAllAttachmentSelect = (checked) => {
		if (attachmentList?.length) {
			const updatedItems = produce(attachmentList, (draft) => {
				draft?.map((s) => {
					if (
						attachmentList?.some((item) => item.id === s.id)
					) {
						s["$selected"] = checked;
					}
					return s;
				});
			});
			setAttachmentList(updatedItems);
		}
	};

	/*=================================================
		Remove File
	/================================================*/
	const [openModal, setOpenModal] = useState(false)
	const [removeFiles, setRemoveFiles] = useState([])

	function setRemoveSelectedFile() {
		const selectedAttachment = attachmentList?.length && attachmentList?.filter((item) => item?.$selected === true).map((selectedItem) => {
			return selectedItem?.filename || selectedItem?.path;
		})

		if (selectedAttachment.length > 0) {
			setRemoveFiles(selectedAttachment)
			handleShowVerifyModal()
		}
	}

	const handleShowVerifyModal = () => {
		setOpenModal(true)
	}

	const handleHideVerifyModal = () => {
		setOpenModal(false)
	}

	const handleVerifyRemoveAttachment = () => {
		const newAttachmentList = attachmentList?.filter((attachment) => !(removeFiles.includes(attachment?.path ?? attachment?.filename)))
		setAttachmentList(newAttachmentList)
		setRemoveFiles([])
		handleHideVerifyModal()
	}

	/*=================================================
		Download File
	/================================================*/
	async function handleDownloadSingleFile(item, name) {

		setLoading(true)
		const [error, result] = await apiDownload.download(item)
		setLoading(false)

		if (error) {
			return setError(error?.error?.message || "Unable to download file(s)")
		}

		const data = `data:application/pdf;base64,${result}`
		const link = document.createElement("a")
		link.href = data
		link.download = name
		link.click()
	}

	async function handleDownloadAllFile() {
		const selectedAttachment = attachmentList?.length && attachmentList?.filter((item) => item?.$selected === true).map((selectedItem) => {
			return selectedItem?.file_content;
		})

		if (selectedAttachment.length > 0) {
			setLoading(true)
			const [error, result] = await apiDownload.downloadFiles({ "files": selectedAttachment })
			setLoading(false)

			if (error) {
				return setError(error?.error?.message || "Unable to download file(s)")
			}

			const data = `data:application/zip;base64,${result}`
			const link = document.createElement("a")
			link.href = data
			link.download = `${dataElement}.zip`
			link.click()
		}
	}

	return (
		<>
			<div className={`attachment-card attachment-card--flex ${attachmentList?.length === 0 && 'no-attachment'}`}>
				{
					attachmentList?.length === 0 ? (
						<>
							{makerChecker && stateType === 'edit' && (
								<div className="attachment-card__btn-all">
									{renderUploadAttachment()}
								</div>
							)}
							{makerChecker && stateType === 'view' && (
								<div className="attachment-card__btn-download-all download-button-all-disabled">
									<div className="attachment-card__btn-icon DS__icon--theme"><IconDownloadAll /></div>
									<div className="attachment-card__btn-text">Download</div>
								</div>
							)}
							<div className="attachment-card__header">No Attachments</div>
						</>) :
						(<>
							{makerChecker && (
								<div className="attachment-card__btn-all">
									{
										stateType === 'edit' ? (
											<>
												<div>{renderUploadAttachment()}</div>

												<div className={`attachment-card__btn-delete-all ${!isChecked && 'download-button-all-disabled'}`}
													data-testid={`delete-button-all`}
													onClick={(e) => {
														setRemoveSelectedFile()
													}}
													onKeyDown={(e) => {
														e.stopPropagation();
														setRemoveSelectedFile();
													}}
												>
													<div className="attachment-card__btn-icon DS__icon--theme"><IconTrashBin /></div>
													<div className="attachment-card__btn-text">Delete</div>
												</div>
											</>) : (
											<div className={`attachment-card__btn-download-all ${!isChecked && 'download-button-all-disabled'}`}
												data-testid={`download-button-all`}
												onClick={(e) => {
													e.stopPropagation();
													handleDownloadAllFile();
												}}
												onKeyDown={(e) => {
													e.stopPropagation();
													handleDownloadAllFile();
												}}
											>
												<div className="attachment-card__btn-icon DS__icon--theme"><IconDownloadAll /></div>
												<div className="attachment-card__btn-text">Download</div>
											</div>
										)
									}
								</div>)}

							<div className="attachment-card__header">
								{
									makerChecker && (
										<Checkbox
											data-testid="attachment-card-checkbox-all"
											selected={isAllChecked(attachmentList)}
											disabled={attachmentList?.every((s) => s?.$disabled) ?? false}
											indeterminate={!isAllChecked(attachmentList) && attachmentList?.some((item) => item?.$selected)}
											handleUpdateItemSelect={(checked) => {
												handleAllAttachmentSelect(checked)
												setIsChecked(checked)
											}}
										/>
									)
								}
								Attachments ({attachmentList.length})
							</div>

							<div className="attachment-card__content">
								<div className="attachment-card__content__rows">
									{attachmentList?.map((item, index) => {
										const attachmentRowClassName = classname({
											"attachment-card__content__row": true,
											"attachment-card__content__row--new": previousAttachmentList && !previousAttachmentList.find((prevItem) => prevItem.file_content === item.file_content) && !item.hasOwnProperty("isGeneratedTT"),
										});
										return (
											<div className={attachmentRowClassName} key={uniqueId()}>
												{
													makerChecker && (
														<span>
															<Checkbox
																data-testid={`attachment-card-checkbox-${item.filename}`}
																selected={item?.$selected}
																disabled={item?.$disabled}
																handleUpdateItemSelect={(checked) => {
																	handleSingleAttachmentSelect(item)
																	setIsChecked(checked)
																}}
															/>
														</span>
													)
												}

												<span>
													<SplitFileType
														text={item.filename || item.name}
														maxWidth={200}
														data-testid={`split-file-type-${item.filename || item.name}`}
													/>
												</span>

												<div className="attachment-card__content__icons">
													{stateType === "view" && !makerChecker && (
														<span>
															<DataElementTopRightSquare
																data-testid={`download-button-${item.filename}`}
																onClick={(e) => {
																	e.stopPropagation();
																	handleDownloadSingleFile(item.file_content, item.filename);
																}}
															/>
														</span>
													)}

													{stateType === "view" && makerChecker && (
														<span className="attachment-card__btn-download">
															<DownloadWhite
																className={item?.$disabled && 'download-button-all-disabled'}
																data-testid={`download-button-${item.filename}`}
																onClick={(e) => {
																	e.stopPropagation();
																	!(item?.$disabled) && handleDownloadSingleFile(item.file_content, item.filename);
																}}
															/>
														</span>
													)}

													{stateType === "edit" && makerChecker && (
														<>
															{item.file_content && <span className="attachment-card__btn-download">
																<DownloadWhite
																	className={item?.$disabled && 'download-button-all-disabled'}
																	data-testid={`download-button-${item.filename}`}
																	onClick={(e) => {
																		e.stopPropagation();
																		!(item?.$disabled) && handleDownloadSingleFile(item.file_content, item.filename);
																	}}
																/>
															</span>}

															<span className={`attachment-card__btn-delete`}>
																<IconTrashBin
																	data-testid={`delete-button-${item.filename || item.name}`}
																	onClick={() => {
																		handleShowVerifyModal()
																		setRemoveFiles([item.filename || item.name])
																	}}
																/>
															</span>
														</>
													)}
												</div>
											</div>
										)
									})}
								</div>
							</div >
						</>)
				}
			</div>

			{
				loading && (
					ReactDOM.createPortal(
						<div className='modal white'>
							<div className='loading'>
								<img src="/loading.gif" alt="loader" />
							</div>
						</div>
						, document.body
					)
				)
			}

			<Toast2 show={error} errorMessage={error} title={"Invalid File"} warningIcon={true} close={() => { handleError() }} />

			<Modal visible={openModal} variant="medium" close={handleHideVerifyModal}>
				<CardConfigure >
					<CardConfigure.Header>
						<CardConfigure.Header.Title>
							<div className="card-configure__header__left">
								<div className="card-configure__title">
									<div className="card-configure__title__left">
										<div className={'card-configure__icon--error'}>
											<IconDelete />
										</div>

									</div>
									<div className="card-configure__title__right">
										<div className="card-configure__title__right__top">
											Delete Attachment
										</div>

									</div>
								</div>
							</div>
						</CardConfigure.Header.Title>
					</CardConfigure.Header>
					<CardConfigure.Body>
						<CardConfigure.Body.Col>
							<div className='share__card-configure-text'>
								{
									removeFiles.length === 1 ? (
										<>{`Are you sure you want to delete ${removeFiles} ?`}</>
									) : (<>
										<div>Are you sure you want to delete below items:</div>
										<br />
										{
											removeFiles.map((item, index) => {
												return (
													<div key={uniqueId()}>{index + 1}. {item}</div>
												)
											})
										}
									</>)
								}
							</div>
						</CardConfigure.Body.Col>
					</CardConfigure.Body>
					<CardConfigure.Footer>
						<CardConfigure.Footer.Right>
							<Button ghost addClass="attachment-card__delete-border" text="Cancel"
								submit={() => {
									handleHideVerifyModal()
								}}
							/>
							<span style={{ width: rem(10), display: 'inline-block' }}></span>
							<Button ghost
								addClass="attachment-card__delete-border"
								text="Confirm"
								warning
								submit={() => {
									handleVerifyRemoveAttachment()
								}}
							/>
						</CardConfigure.Footer.Right>
					</CardConfigure.Footer>
				</CardConfigure>
			</Modal>
		</>
	);
};

AttachmentCard.propTypes = {
	attachmentList: PropTypes.object,
	setAttachmentList: PropTypes.func,
	setUploadAttachment: PropTypes.func,
	setDeleteAttachment: PropTypes.func,
	stateType: PropTypes.string,
	makerChecker: PropTypes.bool,
	dataElement: PropTypes.string,
	attachmentExtensions: PropTypes.object,
	previousAttachmentList: PropTypes.array
};

export default AttachmentCard;
